import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";

import { useAuth } from "./AuthContext";

import {
  getOrCreateUserAccount,
  subscribeToUserAccount,
  setUserPlan,
  incrementUsage,
} from "../firebase/subscription";

import {
  getPlanCapabilities,
  nextResetDate,
} from "../features/subscription/planConfig";

// ============================================================
// CENTRAL SUBSCRIPTION STATE
// ============================================================
//
// CURRENT USER
//       ↓
// SUBSCRIPTION STATE
//       ↓
// PLAN CAPABILITIES
//       ↓
// FEATURE ACCESS
//
// Firestore is the source of truth for subscription state.
// Paddle webhook → Firestore → onSnapshot → React UI
//

const SubscriptionContext = createContext();

const CANCEL_SUBSCRIPTION_FUNCTION_URL =
  "https://us-central1-insightpulse-ai-5960.cloudfunctions.net/cancelPaddleSubscription";

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();

  const [plan, setPlan] = useState("free");

  const [usage, setUsage] = useState({
    period: null,
    count: 0,
  });

  const [scheduledToCancel, setScheduledToCancel] = useState(false);

  const [currentPeriodEnd, setCurrentPeriodEnd] = useState(null);

  const [loading, setLoading] = useState(true);

  // ============================================================
  // LOAD AND LISTEN TO SUBSCRIPTION ACCOUNT
  // ============================================================

  useEffect(() => {
    if (!user) {
      setPlan("free");
      setUsage({
        period: null,
        count: 0,
      });
      setScheduledToCancel(false);
      setCurrentPeriodEnd(null);
      setLoading(false);

      return;
    }

    let unsubscribe;

    const initializeSubscription = async () => {
      try {
        // Make sure the user's document exists first.
        await getOrCreateUserAccount(user.uid);

        // Listen for real-time Firestore updates.
        //
        // Paddle webhook updates Firestore.
        // onSnapshot receives the change immediately.
        unsubscribe = subscribeToUserAccount(
          user.uid,
          (account) => {
            setPlan(account.plan || "free");

            setUsage(
              account.usage || {
                period: null,
                count: 0,
              },
            );

            setScheduledToCancel(
              account.scheduledToCancel || false,
            );

            setCurrentPeriodEnd(
              account.currentPeriodEnd || null,
            );

            setLoading(false);
          },
        );
      } catch (err) {
        console.error(
          "Failed to initialize subscription account:",
          err,
        );

        setLoading(false);
      }
    };

    initializeSubscription();

    // Stop listening when the user logs out or provider unmounts.
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user]);

  // ============================================================
  // PLAN CAPABILITIES
  // ============================================================

  const capabilities = getPlanCapabilities(plan);

  const remaining = Math.max(
    capabilities.monthlyAnalysisLimit - usage.count,
    0,
  );

  const limitReached = remaining <= 0;

  // ============================================================
  // CONSUME ANALYSIS CREDIT
  // ============================================================
  //
  // Called only when a confirmed data analysis is performed.
  //

  const consumeAnalysisCredit = useCallback(async () => {
    if (!user) return;

    const updated = await incrementUsage(user.uid);

    setUsage(updated);
  }, [user]);

  // ============================================================
  // UPGRADE TO PREMIUM
  // ============================================================
  //
  // This is kept for development/testing.
  //
  // In the real payment flow, Paddle webhook should update:
  //
  // plan: "free" → "premium"
  //
  // and the Firestore listener will update the UI automatically.
  //

  const upgradeToPremium = useCallback(async () => {
    if (!user) return;

    await setUserPlan(user.uid, "premium");

    setPlan("premium");
  }, [user]);

  // ============================================================
  // CANCEL SUBSCRIPTION
  // ============================================================
  //
  // IMPORTANT:
  //
  // This does NOT immediately downgrade the user.
  //
  // Firebase calls Paddle.
  // Paddle schedules cancellation at the end of the current
  // billing period.
  //
  // The user remains Premium until the subscription actually ends.
  //

  const downgradeToFree = useCallback(async () => {
    if (!user) {
      throw new Error("User is not authenticated");
    }

    const token = await user.getIdToken();

    const response = await fetch(
      CANCEL_SUBSCRIPTION_FUNCTION_URL,
      {
        method: "POST",

        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );

    const result = await response.json();

    if (!response.ok) {
      console.error("Cancellation API error:", result);

      throw new Error(
        result.details?.error?.detail ||
        result.details?.error?.message ||
        result.details?.error?.code ||
        result.error ||
        "Failed to cancel subscription",
      );
    }

    // We intentionally DO NOT do:
    //
    // setPlan("free")
    //
    // because the user still has Premium access.
    //
    // The Paddle webhook will update Firestore:
    //
    // scheduledToCancel: true
    //
    // The onSnapshot listener will then update the UI.

    return result;
  }, [user]);

  // ============================================================
  // CONTEXT VALUE
  // ============================================================

  const value = {
    plan,

    capabilities,

    usage,

    remaining,

    limitReached,

    scheduledToCancel,

    currentPeriodEnd,

    resetDate: nextResetDate(),

    loading,

    consumeAnalysisCredit,

    upgradeToPremium,

    downgradeToFree,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export const useSubscription = () =>
  useContext(SubscriptionContext);